import { showToast, injectTranslation } from '@repo/commons/utils'
import type {
	ToastModel,
	ValidatorFunction,
	FormValidationItem,
	FormValidationResult,
	ValidatorFunctionFactory
} from '@repo/commons/models'
import { Modality } from '@semanticedge/persistence-service-noheaders-client/src'
import { t } from 'svelte-i18n'
import { get } from 'svelte/store'
import { RELEASE_VERSION_PATTERN, toReleaseVersion } from './version.ts'

export const ALERT_MESSAGE_SEPARATOR: string = '||'

export const joinErrorMessageStrings = (errors?: Record<string, string>): string => {
	if (!errors) return ''
	return Object.values(errors).join(ALERT_MESSAGE_SEPARATOR)
}

export const formatValidationErrorMessage = (
	translationKey: string,
	payload: Record<string, string>
): string => {
	const message = injectTranslation(translationKey, payload)
	if (!payload.modality) return message

	return `${message}${injectTranslation('FormValidation.Modality', payload)}`
}

const buildModalityPayload = (modalityKey: Modality): Record<string, string> => {
	const payload: Record<string, string> = { modality: modalityKey }
	return payload
}

export const validateForm = (
	schema: Record<string, FormValidationItem>,
	touchedFields: Record<string, boolean>,
	isSubmitting: boolean,
	returnAllErrors: boolean = false // NEW
): Record<string, string> => {
	const errors: Record<string, string> = {}

	const getValidators = (validators?: ValidatorFunctionFactory[]): ValidatorFunctionFactory[] =>
		validators && validators.length ? validators : [validateEmptyString]

	Object.entries(schema).forEach(([key, item]) => {
		const { formComponent, validators, translationKeyName, customErrorMessage } = item
		const checks: ValidatorFunctionFactory[] = getValidators(validators)
		const baseName: string = translationKeyName ?? key

		// Per-modality form component: Record<Modality, string | string[]>
		if (formComponent && typeof formComponent === 'object' && !Array.isArray(formComponent)) {
			const perModalityValues: Record<Modality, unknown> = formComponent as Record<
				Modality,
				unknown
			>
			Object.entries(perModalityValues).forEach(([modalityKey, modalityValue]) => {
				const fieldKey = `${key}.${modalityKey}`
				const shouldValidate = isSubmitting || touchedFields[fieldKey]
				if (!shouldValidate) return

				const payload = buildModalityPayload(modalityKey as any)

				const fieldErrors: string[] = []

				for (const factory of checks) {
					const validator = factory(customErrorMessage, payload)
					const error = validator(modalityValue as any, baseName)
					if (error) {
						if (!returnAllErrors) {
							errors[fieldKey] = error
							break
						} else {
							fieldErrors.push(error)
						}
					}
				}

				if (returnAllErrors && fieldErrors.length) {
					errors[fieldKey] = fieldErrors.join(', ')
				}
			})
		} else {
			const fieldKey = key
			const fieldValue = (formComponent ?? '') as string
			const shouldValidate = isSubmitting || touchedFields[fieldKey]
			if (!shouldValidate) return

			const fieldErrors: string[] = []

			for (const factory of checks) {
				const validator = factory(customErrorMessage)
				const error = validator(fieldValue, baseName)
				if (error) {
					if (!returnAllErrors) {
						errors[fieldKey] = error
						break
					} else {
						fieldErrors.push(error)
					}
				}
			}

			if (returnAllErrors && fieldErrors.length) {
				errors[fieldKey] = fieldErrors.join(', ')
			}
		}
	})

	if (isSubmitting) {
		Object.keys(errors).forEach((key) => (touchedFields[key] = true))
	}

	return errors
}

/**
 * `{}` means valid; a nonempty object allows saving with errors shown in project alerts.
 * Strict validation currently does not block submission, so errors can be saved to project alerts.
 * Live validation always returns the errors for display beside the fields.
 * Pass `{}` for absent children so they do not block submission.
 */
export const getFormValidationResult = (
	errors: Record<string, string>,
	schema: Record<string, FormValidationItem>,
	isSubmitting: boolean,
	childResults: FormValidationResult[] = []
): FormValidationResult => {
	const allErrors = childResults.reduce<Record<string, string>>(
		(merged, childErrors) => ({ ...merged, ...childErrors }),
		errors
	)
	const hasStrictError = Object.entries(schema).some(([key, field]) => {
		if (!field.strictlyRequired) return false
		if (Object.hasOwn(allErrors, key)) return true
		// validateForm expands modality fields into keys such as `prompt.VOICE`.
		return (
			typeof field.formComponent === 'object' &&
			field.formComponent !== null &&
			Object.keys(field.formComponent).some((modality) =>
				Object.hasOwn(allErrors, `${key}.${modality}`)
			)
		)
	})
	// Temporarily allow side sheets to save even when a strictly required field has errors.
	const blockStrictErrors = false
	if (blockStrictErrors && isSubmitting && (hasStrictError || childResults.includes(undefined))) {
		return undefined
	}

	return allErrors
}

// Safe deep merge utility to sync nested form states.
// Avoids prototype pollution and handles (objects - arrays) explicitly.
export function syncFormState(target: Record<string, any>, source: Record<string, any>) {
	const isObject = (obj: unknown): obj is Record<string, any> =>
		obj !== null && typeof obj === 'object' && !Array.isArray(obj)

	for (const key in source) {
		if (!Object.prototype.hasOwnProperty.call(source, key)) continue
		const dangerousKeys = ['__proto__', 'constructor', 'prototype']
		if (dangerousKeys.indexOf(key) !== -1) continue

		const sourceVal = source[key]
		const targetVal = target[key]

		if (isObject(sourceVal)) {
			if (!isObject(targetVal)) {
				target[key] = {}
			}
			syncFormState(target[key], sourceVal)
		} else if (Array.isArray(sourceVal)) {
			target[key] = [...sourceVal]
		} else {
			target[key] = sourceVal
		}
	}

	return target
}

/**
 * Builds a value-based validator factory.
 *
 * Usage patterns:
 * 1. Default behavior (no args):
 *    validateEmptyString()
 *    validateMinChars(5)()
 *
 * 2. Custom error key:
 *    validateEmptyString('MyApp.Errors.EmptyName')
 *    validateMinChars(5)('MyApp.Errors.TooShort')
 *
 * 3. Custom error + payload:
 *    validateMinChars(5)(
 *      'MyApp.Errors.TooShort',  // customError
 *      { count: '5' }            // payload
 *    )
 *
 * 4. Payload only (skip custom error and use defaultKey):
 *    validateEmptyString(
 *      undefined,
 *      { someParam: 'value' }
 *    )
 *
 * The payload passed to the factory is merged with:
 * - the resolved field name (`name`)
 * - any payload produced by `payloadFactory`.
 *
 * @param testFn
 *   Predicate returning `true` if `value` is valid.
 * @param defaultKey
 *   Translation key used when no customError is provided.
 * @param payloadFactory
 *   Optional function producing extra translation params.
 * @returns
 *   A ValidatorFunctionFactory with signature
 *   `(customError?: string, payload?: Record<string,string>) => ValidatorFunction`.
 */
const makeValidator = (
	testFn: (value: unknown) => boolean,
	defaultKey: string,
	payloadFactory: (args: { value: string; name: string }) => Record<string, string> = () => ({})
): ValidatorFunctionFactory => {
	return (customError?: string, payload?: Record<string, string>): ValidatorFunction => {
		return (value: string, name: string): string | null => {
			// 1) If it passes the test, it’s valid
			if (testFn(value)) {
				return null
			}

			// 2) Resolve the field-name from `Item.${name}`
			const resolvedKey: string = `Item.${name}`
			const fieldName: string = get(t)(resolvedKey)

			// 3) Build the default payload { name, ...other } and merge any explicit payload
			const defaultParams: Record<string, string> = payloadFactory({ value, name })
			const mergedPayload: Record<string, string> = {
				name: fieldName,
				...defaultParams,
				...(payload ?? {})
			}

			return formatValidationErrorMessage(customError ?? defaultKey, mergedPayload)
		}
	}
}

/**
 * @inheritdoc makeValidator
 */
export const validateEmptyString: ValidatorFunctionFactory = makeValidator(
	(v: unknown): boolean => {
		if (typeof v !== 'string') {
			return false
		}
		return v.trim().length > 0
	},
	'FormValidation.Generic.EmptyString'
)

/**
 * @inheritdoc makeValidator
 */
export const validateInteger: ValidatorFunctionFactory = makeValidator((v: unknown): boolean => {
	if (typeof v !== 'string') {
		return false
	}
	return v === '' || /^-?\d+$/.test(v)
}, 'FormValidation.Generic.InvalidInteger')

/**
 * @inheritdoc makeValidator
 */
export const validateFloat: ValidatorFunctionFactory = makeValidator((v: unknown): boolean => {
	if (typeof v !== 'string') {
		return false
	}
	return v === '' || /^-?\d+(\.\d+)?$/.test(v)
}, 'FormValidation.Generic.InvalidFloat')

/**
 * @inheritdoc makeValidator
 */
export const validateMinChars = (min: number): ValidatorFunctionFactory =>
	makeValidator(
		(v: unknown): boolean => {
			if (typeof v !== 'string') {
				return false
			}
			return v.length >= min
		},
		'FormValidation.Generic.MinChars',
		(): Record<string, string> => ({ count: min.toString() })
	)

export const validateDisallowedChars = (disallowedChars: string): ValidatorFunctionFactory =>
	makeValidator(
		(value: unknown): boolean =>
			typeof value === 'string' &&
			![...disallowedChars].some((character: string) => value.includes(character)),
		'FormValidation.Generic.DisallowedChars',
		({ value }: { value: string }): Record<string, string> => ({
			characters: [
				...new Set([...disallowedChars].filter((character: string) => value.includes(character)))
			]
				.map((character: string) => `'${character}'`)
				.join(', ')
		})
	)

export const validateVersion: ValidatorFunctionFactory = makeValidator(
	(value: unknown): boolean =>
		typeof value === 'string' && RELEASE_VERSION_PATTERN.test(toReleaseVersion(value)),
	'FormValidation.Generic.InvalidVersion'
)

/**
 * @inheritdoc makeValidator
 */
export const validateInList = <T extends { name?: string; title?: string }>(
	list: readonly T[]
): ValidatorFunctionFactory =>
	makeValidator((v: unknown): boolean => {
		if (typeof v !== 'string') {
			return false
		}
		return list.some((item) => item.name === v || item.title === v)
	}, 'FormValidation.Generic.InList')

/**
 * @inheritdoc makeValidator
 */
export const validateListSize = (min: number): ValidatorFunctionFactory =>
	makeValidator(
		(v: unknown): boolean => Array.isArray(v) && v.length >= min,
		'FormValidation.Generic.MinListSize',
		(): Record<string, string> => ({ count: min.toString() })
	)

/**
 * @inheritdoc makeValidator
 */
export const validateEmail: ValidatorFunctionFactory = makeValidator((v: unknown): boolean => {
	if (typeof v !== 'string') {
		return false
	}
	return v === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
}, 'FormValidation.Generic.InvalidEmail')

/**
 * Validates string has at least one uppercase letter
 * @inheritdoc makeValidator
 */
export const validateUppercase: ValidatorFunctionFactory = makeValidator((v: unknown): boolean => {
	if (typeof v !== 'string') {
		return false
	}
	return /[A-Z]/.test(v)
}, 'FormValidation.Generic.MissingUppercase')

/**
 * Validates string has at least one lowercase letter
 * @inheritdoc makeValidator
 */
export const validateLowercase: ValidatorFunctionFactory = makeValidator((v: unknown): boolean => {
	if (typeof v !== 'string') {
		return false
	}
	return /[a-z]/.test(v)
}, 'FormValidation.Generic.MissingLowercase')

/**
 * Validates string has at least one number
 * @inheritdoc makeValidator
 */
export const validateNumber: ValidatorFunctionFactory = makeValidator((v: unknown): boolean => {
	if (typeof v !== 'string') {
		return false
	}
	return /\d/.test(v)
}, 'FormValidation.Generic.MissingNumber')

/**
 * Validates string has at least one special character
 * @inheritdoc makeValidator
 */
export const validateSpecialChar: ValidatorFunctionFactory = makeValidator(
	(v: unknown): boolean => {
		if (typeof v !== 'string') {
			return false
		}
		return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(v)
	},
	'FormValidation.Generic.MissingSpecialChar'
)

/**
 * Validates string length within min and max range
 * @inheritdoc makeValidator
 */
export const validateLengthRange = (min: number, max: number): ValidatorFunctionFactory =>
	makeValidator(
		(v: unknown): boolean => {
			if (typeof v !== 'string') {
				return false
			}
			return v.length >= min && v.length <= max
		},
		'FormValidation.Generic.LengthRange',
		() => ({
			min: String(min),
			max: String(max)
		})
	)

export const validateMatch = (compareValue: unknown): ValidatorFunctionFactory => {
	return makeValidator((v: unknown): boolean => {
		if (typeof v !== 'string') {
			return false
		}
		return v === '' || v === (compareValue as string)
	}, 'FormValidation.Generic.NoMatch')
}
